import { RiErrorWarningLine, RiPushpinFill } from "@remixicon/react";
import type { DensitySnapshot } from "@screener/protocol";
import { Tooltip, TooltipContent, TooltipTrigger } from "@screener/uikit/components/tooltip";
import { cn } from "@screener/uikit/lib/utils";
import {
  type ComponentPropsWithoutRef,
  memo,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from "react";
import { formatUsd, stateLabel } from "@/lib/labels";
import { CARD_ROW_SHELL_CLASS, CARD_STRIP_CLASSES } from "./card-classes";
import { cardFillFraction } from "./cards";
import { dispatchRowKeyAction, type RowKeyActionHandlers } from "./density-row";
import { isUnconfirmed, secondsSince } from "./derive";
import { Elapsed } from "./elapsed";
import {
  cardRowTitle,
  formatAge,
  formatCardPrice,
  formatDistance,
  formatPrice,
  SIDE_WORDS,
} from "./format";
import { type RowPinMatch, rowPinState } from "./pin-match";
import { RoundMark } from "./round-mark";
import { MmMark } from "./side-badge";
import { STALE_HINT } from "./stale-mark";
import { useNowText } from "./use-now";

/**
 * Строка карточки §9 (1.45, 1.75) — четыре величины в одну линию на полосе
 * цвета стороны: аск красный, бид бирюзовый. Порядок — цена, время жизни,
 * дистанция, объём (§88 п. 3). Слова стороны и второго ряда нет;
 * состояние и подходы — в детальной панели. Признак ММ — значком «ММ» у
 * объёма (§123); перечень кодов флагов — в подсказке значка и в панели. Действий-кнопок внутри
 * строки нет: кнопка внутри кнопки недопустима, и действия строки —
 * копировать тикер, заметки, закрепление, избранное — доступны только
 * хоткеями и через детальную панель (design.md change stage6-density-cards,
 * решение 9).
 */

interface DensityCardRowProps {
  row: DensitySnapshot;
  /** Объём самой крупной плотности карточки, USD: от него — доля заливки. */
  maxUsd: number;
  /** Инструмент перестал отдавать данные: строка есть, но она устарела. */
  stale: boolean;
  /**
   * Плотность закончилась после заморозки вида (§77 п. 4): строка стоит на
   * месте последним снимком — бледная, зачёркнутая, без открытия панели.
   */
  ended: boolean;
  /** Эта строка сейчас единственная точка табуляции вида. */
  focusable: boolean;
  /**
   * Обработчики принимают строку или её `density_id` аргументом, а не
   * замыкают их: строка обёрнута в `memo`, и замыкание, созданное родителем
   * на каждый рендер, перерисовывало бы все строки вида.
   */
  onFocus: (densityId: string) => void;
  onKeyDown: (densityId: string, event: ReactKeyboardEvent<HTMLElement>) => void;
  /** Открыть детальную панель §9 по этой плотности. */
  onOpen: (row: DensitySnapshot) => void;
  /** Панель открыта по этой строке: она обязана оставаться видимой. */
  selected: boolean;
  /**
   * Уровень строки закреплён §6.7 — сопоставление клиента, не признак
   * снимка. `"unknown"` — список закреплений ещё не прочитан или недоступен.
   */
  pinMatch: RowPinMatch | "unknown" | undefined;
  /** Закрепить уровень строки или снять уже стоящее закрепление — одно действие. */
  onTogglePin: (row: DensitySnapshot) => void;
  /** Открыть детальную панель на разделе заметок §9. */
  onOpenNotes: (row: DensitySnapshot) => void;
  /** Скопировать тикер строки в буфер обмена (§9 1.44, `tickerForClipboard`). */
  onCopyTicker: (row: DensitySnapshot) => void;
  /** Поставить или снять избранное монеты строки целиком — одно действие. */
  onToggleFavorite: (row: DensitySnapshot) => void;
  /** Скрыть монету строки целиком — хоткей `h` (§75 п. 2). */
  onHideCoin: (base: string) => void;
}

/**
 * Дистанция «близко» — модуль не больше 0.3 % (§88 п. 3, design.md
 * stage9-redesign-cards, Д7): такая дистанция набрана полужирным основным
 * цветом, дальняя и пустая — приглушённым.
 */
export const CARD_NEAR_DISTANCE_PCT = 0.3;

/**
 * Кнопка строки. Возраст входит в доступное имя (`aria-label`), поэтому имя
 * обязано тикать вместе с часами, а строка целиком — нет: кнопка читает
 * `useNowText()` сама, а содержимое приходит `children` готовым элементом.
 * React не перерисовывает потомков с той же ссылкой на элемент, так что тик
 * стоит одной кнопки, а не её четырёх ячеек.
 */
function AgedRowButton({
  detectedAtMs,
  labelHead,
  labelTail,
  children,
  ...buttonProps
}: {
  detectedAtMs: number;
  /** Часть имени до возраста: сторона, полная цена. */
  labelHead: string;
  /** Часть имени после возраста: дистанция, объём, пометки. */
  labelTail: string;
  children: ReactNode;
} & Omit<ComponentPropsWithoutRef<"button">, "aria-label" | "children">) {
  const age = useNowText((now) => formatAge(secondsSince(detectedAtMs, now)));
  return (
    <button type="button" aria-label={`${labelHead}, ${age}, ${labelTail}`} {...buttonProps}>
      {children}
    </button>
  );
}

function noop(): void {}

/**
 * Хоткеи строки карточки. Ушедшая строка (§77 п. 4) не открывает панель и не
 * трогает закрепление уровня: плотности уже нет, а закрепление по её уровню
 * создало бы дубль вместо снятия. Копирование тикера, избранное и скрытие
 * монеты работают как у живой — они про монету, а не про плотность.
 */
export function cardRowKeyHandlers(
  row: DensitySnapshot,
  ended: boolean,
  actions: Pick<
    DensityCardRowProps,
    "onOpen" | "onTogglePin" | "onOpenNotes" | "onCopyTicker" | "onToggleFavorite" | "onHideCoin"
  >,
): RowKeyActionHandlers {
  return {
    onOpen: ended ? noop : () => actions.onOpen(row),
    onTogglePin: ended ? noop : () => actions.onTogglePin(row),
    onOpenNotes: ended ? noop : () => actions.onOpenNotes(row),
    onCopyTicker: () => actions.onCopyTicker(row),
    onToggleFavorite: () => actions.onToggleFavorite(row),
    onHideCoin: () => actions.onHideCoin(row.instrument.base),
  };
}

function DensityCardRowView({
  row,
  maxUsd,
  stale,
  ended,
  focusable,
  onFocus,
  onKeyDown,
  onOpen,
  selected,
  pinMatch,
  onTogglePin,
  onOpenNotes,
  onCopyTicker,
  onToggleFavorite,
  onHideCoin,
}: DensityCardRowProps) {
  const side = row.side;
  const { match, pinned, driftText, diverged } = rowPinState(row, pinMatch);
  // Число сдвига в строке не стоит (web-density-cards): оно в подсказке и подписи значка.
  const pinHint =
    match !== null && driftText !== null
      ? `Закреплено на ${formatPrice(match.pin.price)}, якорь ушёл на ${driftText}`
      : null;
  // Подсказка значка есть всегда, пока значок есть: иначе наведение на него показывает цену строки.
  const pinTitle =
    pinHint ?? (match !== null ? `Закреплено на ${formatPrice(match.pin.price)}` : undefined);

  const flagged = row.mm_flags.length > 0;
  const fullPrice = formatPrice(row.anchor_price);
  const distance = formatDistance(row.distance_pct);
  const fillPercent = cardFillFraction(row.density_usd, maxUsd) * 100;
  const strip = CARD_STRIP_CLASSES[side];
  const nearDistance =
    row.distance_pct !== null && Math.abs(row.distance_pct) <= CARD_NEAR_DISTANCE_PCT;

  // Доступное имя: сторона первым словом, затем величины в порядке колонок
  // (цена полная вместо короткой, возраст, дистанция, объём); возраст вставляет `AgedRowButton`. Пометки,
  // которые на виду только значком или прозрачностью, названы словами в конце.
  const labelHead = [SIDE_WORDS[side], fullPrice].join(", ");
  const labelTail = [
    distance,
    formatUsd(row.density_usd),
    flagged ? `флаги ММ: ${row.mm_flags.join(", ")}` : null,
    isUnconfirmed(row.state) ? stateLabel(row.state) : null,
    // Полная фраза, со сдвигом якоря: `aria-label` строки заменяет подпись
    // значка, и сдвиг иначе остался бы только цветом кольца.
    pinTitle ?? null,
    stale ? "без данных" : null,
    ended ? "закончилась" : null,
  ]
    .filter((part) => part !== null)
    .join(", ");

  return (
    <AgedRowButton
      detectedAtMs={row.detected_at_ms}
      labelHead={labelHead}
      labelTail={labelTail}
      data-row-key={row.density_id}
      data-side={side}
      data-state-name={row.state}
      title={cardRowTitle(row)}
      // Ушедшая строка остаётся в навигации (фокус не теряется), но панель
      // по ней не открывается: плотности уже нет, а подробности устарели.
      aria-haspopup={ended ? undefined : "dialog"}
      aria-disabled={ended ? "true" : undefined}
      aria-current={selected ? "true" : undefined}
      tabIndex={focusable ? 0 : -1}
      onFocus={() => onFocus(row.density_id)}
      onClick={ended ? undefined : () => onOpen(row)}
      onKeyDown={(event) => {
        if (
          dispatchRowKeyAction(
            event,
            cardRowKeyHandlers(row, ended, {
              onOpen,
              onTogglePin,
              onOpenNotes,
              onCopyTicker,
              onToggleFavorite,
              onHideCoin,
            }),
          )
        ) {
          return;
        }
        onKeyDown(row.density_id, event);
      }}
      className={cn(
        CARD_ROW_SHELL_CLASS,
        strip.track,
        // Фокус — обводкой, а не кольцом: кольцо занято сдвигом якоря, и на
        // строке в фокусе он иначе пропадал бы.
        "focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring",
        isUnconfirmed(row.state) && "opacity-50",
        stale && "opacity-70",
        // Цвет зачёркнутого — тот же `currentColor`; бледность — прозрачность,
        // без литералов цвета.
        ended && "line-through opacity-40",
        // Фон занят полосой, поэтому выбор и сдвиг якоря — внутренние кольца;
        // выбор важнее (design.md stage9-redesign-cards, Д8).
        selected ? "ring-1 ring-inset ring-ring" : diverged && "ring-2 ring-inset ring-pin",
      )}
    >
      {/* Заливка — от правого края на долю от крупнейшей плотности карточки
          (§9 1.9, правка 1.45, §88 п. 4); сплошная черта — у правого края.
          Под текстом: величины лежат поверх, `relative`. */}
      <span
        data-slot="strip-fill"
        aria-hidden="true"
        className={cn(
          // Новая доля встаёт сразу, без transition ширины: на живом потоке
          // плавная заливка на каждой строке стоила ~50 мс/с главного потока
          // (DEN-104, design stage9-cards-layout-no-measure Д3).
          "pointer-events-none absolute inset-y-0 right-0",
          strip.fill,
        )}
        style={{ width: `${fillPercent}%` }}
      />
      <span
        data-slot="strip-edge"
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute inset-0 rounded-[inherit] border-r-2",
          strip.edge,
        )}
      />

      {/* Колонки задаёт `CardContent` карточки (`density-cards.tsx`), строка
          берёт их через `subgrid`: одноимённые величины строк одной карточки
          стоят друг под другом, а остаток ширины забирает цена. */}
      {/* Сжимается только текст цены: отметка круглой цены и значки не
          сжимаются, текст обрезается многоточием, полная цена — в `title`
          текста и в доступном имени строки. */}
      <span
        data-cell="price"
        className="relative flex min-w-0 items-center gap-1 font-semibold whitespace-nowrap"
      >
        {/* Подсказка с полной ценой — на тексте, а не на ячейке: на ячейке она
            перекрывала бы подсказку строки над отметкой круглой цены (`round-mark.tsx`). */}
        <Tooltip>
          <TooltipTrigger render={<span data-slot="price-text" className="min-w-0 truncate" />}>
            {formatCardPrice(row.anchor_price)}
          </TooltipTrigger>
          <TooltipContent>{fullPrice}</TooltipContent>
        </Tooltip>
        {row.round_rank !== null ? (
          <RoundMark rank={row.round_rank} className="shrink-0 text-[9px]" />
        ) : null}
        {pinned && match !== null ? (
          // Подсказка ставится на обёртку: иконка ссылки не принимает.
          <Tooltip>
            <TooltipTrigger render={<span className="inline-flex shrink-0" />}>
              <RiPushpinFill
                role="img"
                aria-label={pinHint ?? `Уровень закреплён на ${formatPrice(match.pin.price)}`}
                className="size-[11px] text-pin"
              />
            </TooltipTrigger>
            <TooltipContent>{pinTitle}</TooltipContent>
          </Tooltip>
        ) : null}
        {stale ? (
          // Значок вместо слова «без данных» прежнего второго ряда: слово
          // удлиняло бы худшую строку (design.md, решение 4). Слово — в подсказке.
          <Tooltip>
            <TooltipTrigger
              render={<span data-slot="stale-mark" className="inline-flex shrink-0" />}
            >
              <RiErrorWarningLine aria-hidden="true" className="size-[11px] text-warning" />
            </TooltipTrigger>
            <TooltipContent>{STALE_HINT}</TooltipContent>
          </Tooltip>
        ) : null}
      </span>

      <span data-cell="age" className="relative text-right text-muted-foreground whitespace-nowrap">
        <Elapsed sinceMs={row.detected_at_ms} />
      </span>
      <span
        data-cell="distance"
        className={cn(
          "relative text-right whitespace-nowrap",
          nearDistance ? "font-semibold text-foreground" : "font-[450] text-muted-foreground",
        )}
      >
        {distance}
      </span>
      <span
        data-cell="volume"
        className={cn(
          "relative text-right font-semibold text-foreground whitespace-nowrap",
          // Значок и число — одна линия, не сжимаются; колонка растёт по содержимому.
          flagged && "flex items-center justify-end gap-1",
        )}
      >
        {flagged ? <MmMark flags={row.mm_flags} /> : null}
        {formatUsd(row.density_usd)}
      </span>
    </AgedRowButton>
  );
}

/** Закрепление одной и той же строки: объекты совпадений пересоздаются на каждый пересчёт. */
export function samePinMatch(
  left: RowPinMatch | "unknown" | undefined,
  right: RowPinMatch | "unknown" | undefined,
): boolean {
  if (left === right) {
    return true;
  }
  if (typeof left !== "object" || typeof right !== "object") {
    return false;
  }
  return left.pin === right.pin && left.aligned === right.aligned;
}

/** Сравнение пропсов строки: всё по ссылке, кроме совпадения закрепления — оно по значению. */
export function areCardRowPropsEqual(
  previous: DensityCardRowProps,
  next: DensityCardRowProps,
): boolean {
  return (
    previous.row === next.row &&
    previous.maxUsd === next.maxUsd &&
    previous.stale === next.stale &&
    previous.ended === next.ended &&
    previous.focusable === next.focusable &&
    previous.selected === next.selected &&
    previous.onFocus === next.onFocus &&
    previous.onKeyDown === next.onKeyDown &&
    previous.onOpen === next.onOpen &&
    previous.onTogglePin === next.onTogglePin &&
    previous.onOpenNotes === next.onOpenNotes &&
    previous.onCopyTicker === next.onCopyTicker &&
    previous.onToggleFavorite === next.onToggleFavorite &&
    previous.onHideCoin === next.onHideCoin &&
    samePinMatch(previous.pinMatch, next.pinMatch)
  );
}

export const DensityCardRow = memo(DensityCardRowView, areCardRowPropsEqual);
